import { queryOptions } from "@tanstack/react-query";
import { fetchEntries } from "@builder.io/sdk-react/edge";
import { clientEnv } from "~/lib/utils";
import {
  getCoachData,
  getOtherOffers,
  getCoaches,
  getOfferByUsernameAndId,
} from "~/shared/loaders";
import {
  getCoachesByCity,
  getCoachesByCategory,
  getCoachesBySubcategory,
  getCoachesByCategoryAndCity,
  getCoachesBySubcategoryAndCity,
  getFoundingCoaches,
} from "~/lib/functions/coach-grouping";
import {
  getCoach,
  getCoachProfile,
  getOffersByCoachId,
  getResourcesByCoachId,
} from "~/lib/functions/coach-backend";
import { MembershipPlan } from "~/lib/schemas";

export type NavItem = {
  id?: string; // Make id optional to match BuilderContent
  data?: {
    label?: string;
    url?: string;
    children?: {
      label: string;
      url: string;
    }[];
  };
};

export const navigationMenuQueryOptions = () =>
  queryOptions({
    queryKey: ["navigation-menu"],
    placeholderData: [],
    queryFn: async (): Promise<NavItem[]> => {
      const builderContent = await fetchEntries({
        model: "navigation-menu",
        apiKey: clientEnv.VITE_BUILDER_API_KEY,
      });

      return builderContent.map((item) => ({
        id: item.id,
        data: {
          label: item.data?.label,
          url: item.data?.url,
          children: item.data?.children?.map(
            (child: { label: string; url: string }) => ({
              label: child.label,
              url: child.url,
            }),
          ),
        },
      }));
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  } as const);

// Coach-related query options
export const coachQueryOptions = {
  // Public coach profile data
  byUsername: (username: string) =>
    queryOptions({
      queryKey: ["coach", "public", username],
      queryFn: () => getCoachData({ data: { username } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  // Public coach offers for Pro+ coaches
  otherOffers: (
    coachId: string,
    mainOfferId?: string | null,
    membershipPlan?: MembershipPlan,
  ) =>
    queryOptions({
      queryKey: ["coach", "offers", coachId, mainOfferId],
      queryFn: () =>
        getOtherOffers({
          data: {
            coachId,
            mainOfferId,
            membershipPlan: membershipPlan || MembershipPlan.Standard,
          },
        }),
      staleTime: 15 * 60 * 1000, // 15 minutes
      gcTime: 60 * 60 * 1000, // 1 hour
      enabled: membershipPlan === MembershipPlan.ProPlus,
    }),

  // All public coaches for homepage
  all: () =>
    queryOptions({
      queryKey: ["coaches", "public", "all"],
      queryFn: () => getCoaches(),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 15 * 60 * 1000, // 15 minutes
    }),

  // Coaches filtered by city
  byCity: (citySlug: string) =>
    queryOptions({
      queryKey: ["coaches", "public", "city", citySlug],
      queryFn: () => getCoachesByCity({ data: { citySlug } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  // Coaches filtered by category
  byCategory: (categorySlug: string) =>
    queryOptions({
      queryKey: ["coaches", "public", "category", categorySlug],
      queryFn: () => getCoachesByCategory({ data: { categorySlug } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  // Coaches filtered by subcategory
  bySubcategory: (subcategorySlug: string) =>
    queryOptions({
      queryKey: ["coaches", "public", "subcategory", subcategorySlug],
      queryFn: () => getCoachesBySubcategory({ data: { subcategorySlug } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  // Coaches filtered by category and city
  byCategoryAndCity: (categorySlug: string, citySlug: string) =>
    queryOptions({
      queryKey: [
        "coaches",
        "public",
        "category",
        categorySlug,
        "city",
        citySlug,
      ],
      queryFn: () =>
        getCoachesByCategoryAndCity({ data: { categorySlug, citySlug } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  // Coaches filtered by subcategory and city
  bySubcategoryAndCity: (subcategorySlug: string, citySlug: string) =>
    queryOptions({
      queryKey: [
        "coaches",
        "public",
        "subcategory",
        subcategorySlug,
        "city",
        citySlug,
      ],
      queryFn: () =>
        getCoachesBySubcategoryAndCity({ data: { subcategorySlug, citySlug } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  founding: () =>
    queryOptions({
      queryKey: ["coaches", "public", "founding"],
      queryFn: () => getFoundingCoaches(),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  // ==========================================
  // BACKEND OPERATIONS (Authenticated)
  // ==========================================

  // Private coach data (with all fields for backend/authenticated use)
  byId: (id: string) =>
    queryOptions({
      queryKey: ["coach", "private", id],
      queryFn: () => getCoach({ data: { id } }),
      staleTime: 2 * 60 * 1000, // 2 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    }),

  // Coach profile data for editing
  profile: (id: string) =>
    queryOptions({
      queryKey: ["coach", "profile", id],
      queryFn: () => getCoachProfile({ data: { id } }),
      staleTime: 1 * 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes
    }),

  // Coach offers for management
  offers: (coachId: string) =>
    queryOptions({
      queryKey: ["coach", "offers", "management", coachId],
      queryFn: () => getOffersByCoachId({ data: { coachId } }),
      staleTime: 1 * 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes
    }),

  // Coach resources for management
  resources: (coachId: string) =>
    queryOptions({
      queryKey: ["coach", "resources", "management", coachId],
      queryFn: () => getResourcesByCoachId({ data: { coachId } }),
      staleTime: 1 * 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes
    }),
} as const;

// Offer-related query options
export const offerQueryOptions = {
  // Public offer by username and offer ID
  byUsernameAndId: (username: string, offerId: string) =>
    queryOptions({
      queryKey: ["offer", username, offerId],
      queryFn: () => getOfferByUsernameAndId({ data: { username, offerId } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
      retry: (failureCount, error) => {
        // Don't retry on 404 errors
        if (error instanceof Response && error.status === 404) {
          return false;
        }
        return failureCount < 3;
      },
    }),
} as const;
